import { useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useQuery, useQueryClient } from 'react-query';
import MeetingService from '../../../services/MeetingService';
import SectionService from '../../../services/SectionService';
import {
  clearAssets,
  addAsset,
} from '../store/slices/assetsSlice';
import {
  clearTimeline,
  selectItem,
  reorderItems,
} from '../store/slices/timelineSlice';
import {
  reset as resetPlayback,
  seek,
} from '../store/slices/playbackSlice';
import {
  resetEditorUI,
} from '../store/slices/editorUISlice';
import { clearHistory } from '../store/slices/undoRedoSlice';
import { convertSectionsToTimeline } from '../lib/convertSections';

export function useMeetingData({
  meetingId,
  isEditMode,
  setMeetingTitle,
  setCurrentSectionsPage,
}) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const prevMeetingIdRef = useRef(null);
  const prevSectionsSignatureRef = useRef('');

  const { data: fetchedMeeting } = useQuery(
    ['meeting', meetingId],
    () => MeetingService.getMeeting(meetingId),
    {
      enabled: isEditMode && !!meetingId,
      refetchOnMount: 'always',
      staleTime: 0,
    }
  );

  const { data: fetchedSections } = useQuery(
    ['sections', meetingId],
    () => SectionService.getSections(meetingId),
    {
      enabled: isEditMode && !!meetingId,
      refetchOnMount: 'always',
      staleTime: 0,
    }
  );

  // Reset state when switching between edit and create mode or between different meetings
  useEffect(() => {
    // Check if meetingId changed (including from undefined to value or vice versa)
    if (prevMeetingIdRef.current !== meetingId) {
      // Clear all Redux state
      dispatch(clearAssets());
      dispatch(clearTimeline());
      dispatch(resetPlayback());
      dispatch(resetEditorUI()); // Reset all editor UI states (modals, context, etc.)
      
      // Reset local state - but only if we're not in edit mode or if we don't have meeting data yet
      // This prevents clearing the title when React Query returns cached data immediately
      if (!isEditMode || !fetchedMeeting) {
        setMeetingTitle('');
      }
      
      // Invalidate React Query cache for the previous meeting if it existed
      if (prevMeetingIdRef.current) {
        queryClient.removeQueries(['meeting', prevMeetingIdRef.current]);
        queryClient.removeQueries(['sections', prevMeetingIdRef.current]);
      }
      
      // Update ref
      prevMeetingIdRef.current = meetingId;
    }
  }, [meetingId, dispatch, queryClient, setMeetingTitle, isEditMode, fetchedMeeting]);

  // Load meeting title when fetched - this must run AFTER the reset effect
  // Always set the title when fetchedMeeting is available, even if it was just cleared by reset
  useEffect(() => {
    if (fetchedMeeting && fetchedMeeting.title) {
      setMeetingTitle(fetchedMeeting.title);
    }
  }, [fetchedMeeting, setMeetingTitle]);

  // Helper function to load regular sections (non-draft)
  const loadRegularSections = useCallback(() => {
    if (!fetchedSections) return;

    const signature = JSON.stringify(
      fetchedSections.map((s) => ({
        id: s.id,
        order: s.order,
        updatedAt: s.updatedAt,
        type: s.type,
        title: s.title,
        questionUpdatedAt: s.Question?.updatedAt,
        assignedEmail: s.assignedEmail,
        assignedUserId: s.assignedUserId,
      }))
    );

    if (signature === prevSectionsSignatureRef.current) return;
    prevSectionsSignatureRef.current = signature;

    // Clear existing state
    dispatch(clearAssets());
    dispatch(clearTimeline());
    dispatch(resetPlayback());
    dispatch(clearHistory()); // Clear undo/redo history when loading sections

    // Convert sections to timeline format
    // Backend returns sections sorted by Section.order ASC, so order is preserved
    // Trim settings are now stored in section.trimSettings, so no need to pass trimMap
    const { assets: convertedAssets, timelineItems: convertedTimelineItems } = convertSectionsToTimeline(
      fetchedSections
    );

    // Add assets to Redux
    convertedAssets.forEach((asset) => {
      dispatch(addAsset(asset));
    });

    // Add timeline items to Redux in correct order
    // CRITICAL: Use reorderItems to preserve the exact order from backend (Section.order)
    // addTimelineItem uses currentTime-based insertion which can reorder items incorrectly
    // Convert timeline items to Redux format first
    const reduxTimelineItems = convertedTimelineItems.map((item) => ({
      id: item.id,
      asset_id: item.asset_id,
      order: item.order, // Preserve order from convertSectionsToTimeline
      start_time: item.start_time,
          duration: item.duration,
      settings: {
        volume: 1,
        trim_start: item.settings.trim_start || 0,
        trim_end: item.settings.trim_end || 0,
      },
      metadata: item.metadata || {},
    }));

    // Set all items at once using reorderItems to preserve exact order from backend
    dispatch(reorderItems(reduxTimelineItems));

    // After batch load completes, seek to beginning, scroll timeline to start, and select first item
    // Use setTimeout to ensure this runs after all Redux state updates
    setTimeout(() => {
      dispatch(seek(0));
      setCurrentSectionsPage(0);
      // Select the first item if any items were loaded
      if (convertedTimelineItems.length > 0) {
        dispatch(selectItem(convertedTimelineItems[0].id));
      }
    }, 100);
  }, [fetchedSections, dispatch, setCurrentSectionsPage]);

  // Convert sections to timeline items and assets when data changes (edit mode)
  // Backend now handles filtering for both PUBLISHED and DRAFT meetings,
  // so we always use the same loading path
  useEffect(() => {
    if (!isEditMode || !fetchedMeeting || !fetchedSections) return;
    loadRegularSections();
  }, [isEditMode, fetchedMeeting, fetchedSections, loadRegularSections]);

  return {
    fetchedMeeting,
    fetchedSections,
  };
}

